import { eAccessControlUserType } from '../enums/eAccessControlUserType';
import { urlGeneratorService } from './urlGeneratorService';
import { UUID } from '../../types/shared/UUID';
import { eAccessControlLogoutReason } from 'ui-toolkit/enums/eAccessControlLogoutReason';
import { convertDataToString } from 'utilities/queryString';
import { PolicyTypeEnum } from '../../state';
import { ISearchOrdersSpecificationData } from '../orders/searchOrder/types/ISearchOrdersSpecification';

export type FeeTabs = 'a-j' | 'k-n' | 'incoming-funds' | 'misc-disbursements' | 'disbursed-transactions';

const DefaultClientUrls = {
  escrowIndex: () => '/spa/admin/accounting',
  lendersIndex: () => '/spa/admin/lenders',
  /** @Deprecated */
  qaIndex: () => 'admin/qa_tools',
  clientCompaniesIndex: () => '/spa/admin/clients',
  administrationIndex: () => '/spa/admin/administration',
  clientCompany: (clientId: UUID) => `/spa/admin/clients/${clientId}`,
  lenderCompany: (lenderId: UUID) => `/spa/admin/lenders/${lenderId}`,
  lenderCompanyCreate: () => `/spa/admin/lenders/new`,
  lenderCompanyEdit: (lenderId: UUID) => `/spa/admin/lenders/${lenderId}/edit`,

  cdItemsV3: (orderId: UUID) => `/spa/admin/order/${orderId}/cd_builder/section-a-j`,
  /** @Deprecated */
  cdItems: (orderId: UUID) => `/cd_items/v3/${orderId}`,
  /** @Deprecated */
  cdItemsWithDetails: (orderId: UUID) => `/cd_items/v3/${orderId}/with_details`,
  /** @Deprecated */
  cdItemsWithDetailsAndLabels: (orderId: UUID) => `/cd_items/v3/${orderId}/with_labels`,
  /** @Deprecated */
  refiDiscountByState: (state: string, fips: string, clientName: string) =>
    `/cd_items/refinance/discount/${state}?fips=${fips}&client=${clientName}`,
  //new fees route
  feesTab: (orderId: UUID, section: FeeTabs = 'a-j') => `/spa/admin/order/${orderId}/fees/${section}`,
  feesTabEdit: (orderId: UUID, section: FeeTabs, feeId: UUID) => `/spa/admin/order/${orderId}/fees/${section}/${feeId}`,
  feesDisburseAll: (orderId: UUID) => `/spa/admin/order/${orderId}/disburse-all`,
  feesTabEditWithNewLineItem: (orderId: UUID, section: FeeTabs, feeId: UUID, statementFeeId?: UUID, create?: string) =>
    `/spa/admin/order/${orderId}/fees/${section}/${feeId}?statementFeeId=${statementFeeId}&create=${create}`,

  adminCommentsInternal: (orderId: UUID) => `/spa/admin/order/${orderId}/comments/internal`,
  adminCommentsExternal: (orderId: UUID) => `/spa/admin/order/${orderId}/comments/external`,
  adminCommentsVendor: (orderId: UUID) => `/spa/admin/order/${orderId}/comments/vendor`,
  documentsIndex: (orderId: UUID) => `/spa/admin/order/${orderId}/documents`,

  activity: (orderId: UUID) => `/spa/admin/order/${orderId}/activity`,
  escrowSPA: (orderId: UUID) => `/spa/admin/order/${orderId}/escrow/edit`,
  escrowSPAView: (orderId: UUID) => `/spa/admin/order/${orderId}/escrow`,
  escrowView: (escrowFileId: string) => `/admin/escrow_files/${escrowFileId}`,

  orderPage: (userType: eAccessControlUserType, orderId: string) => orderPages[userType](orderId),
  ordersPage: (userType: eAccessControlUserType) => `/spa/${userType.toLowerCase()}/orders`,
  // Order Overview Pieces
  orderStatus: (orderId: UUID) => `/spa/admin/order/${orderId}`,
  spruceOrderStatus: (orderId: UUID, spruceInstanceHostname: string) =>
    `https://${spruceInstanceHostname}/spa/admin/order/${orderId}`,
  orderEditAddress: (orderId: UUID) => `/spa/admin/order/${orderId}/property`,
  orderEditTransaction: (orderId: UUID, transactionField: string) =>
    `/spa/admin/order/${orderId}/transaction/${transactionField}`,
  orderTasks: (orderId: UUID) => `/spa/admin/order/${orderId}/tasks`,
  orderTask: (orderId: UUID, taskId: string) => `/spa/admin/order/${orderId}/tasks/${taskId}`,
  orderContacts: (orderId: UUID) => `/spa/admin/order/${orderId}/contacts`,

  // Order Scheduling
  orderScheduling: (orderId: UUID) => `/spa/admin/order/${orderId}/scheduling`,
  orderSchedulingByAppointment: (orderId: UUID, appointmentId: UUID) =>
    `/spa/admin/order/${orderId}/scheduling/${appointmentId}`,

  //no longer title_index
  title: (orderId: UUID) => `/spa/admin/order/${orderId}/title`,

  // workflows
  workflows: (orderId: UUID) => `/spa/admin/order/${orderId}/workflows`,

  searchReport: (userType: eAccessControlUserType, orderId: UUID, action: 'edit' | 'create') =>
    `/spa/${userType.toLowerCase()}/order/${orderId}/report/searchReport/${action}`,
  searchReportExceptions: (userType: eAccessControlUserType, orderId: UUID, action: 'edit' | 'create') =>
    `/spa/${userType.toLowerCase()}/order/${orderId}/report/searchReportExceptions/${action}`,

  taxCertification: (userType: eAccessControlUserType, orderId: UUID, type: 'edit' | 'create') =>
    `/spa/${userType.toLowerCase()}/order/${orderId}/taxCertification/${type}`,
  taxCertificationRequest: (orderId: UUID) => `/vendor/tax_certifications/${orderId}`,
  titleSearchRequests: (orderId: UUID) => `/vendor/orders/${orderId}`,

  policyGeneration: (orderId: UUID, policyType: PolicyTypeEnum, action: 'edit' | 'create') =>
    `/spa/admin/order/${orderId}/policy/${policyType}/scheduleA/${action}`,
  policyGenerationB: (orderId: UUID, policyType: PolicyTypeEnum, action: 'edit' | 'create') =>
    `/spa/admin/order/${orderId}/policy/${policyType}/scheduleB/${action}`,
  policyReview: (orderId: UUID, policyType: PolicyTypeEnum, action: 'edit' | 'create') =>
    `/spa/admin/order/${orderId}/policy/${policyType}/review/${action}`,

  spaLogin: (userType: eAccessControlUserType) => `/spa/${userType.toLowerCase()}/login`,
  logoutAuth0: (logoutReason: eAccessControlLogoutReason, lastLocation?: string) =>
    `/spa/logout?reason=${logoutReason}` + (lastLocation ? `&lastLocation=${lastLocation}` : ''),
  // Create New Order
  createNewOrder: (userType: eAccessControlUserType) => `/spa/${userType.toLocaleLowerCase()}/order/create`,


  // Dashboard Page
  dashboard: () => '/spa/admin/dashboard',

  // Tags
  administrationListTags: () => '/spa/admin/administration/tags',
  administrationCreateNewTag: () => '/spa/admin/administration/tags/create',
  administrationEditTag: (tagId: UUID) => `/spa/admin/administration/tags/${tagId}`,

  // Orders Page by type
  adminOrders: (data?: ISearchOrdersSpecificationData) => {
    const dataString = data ? `?${convertDataToString(data)}` : '';
    return `/spa/admin/orders${dataString}`;
  },
  //vendor portal
  vendorOrders: () => `/spa/vendor/orders`,
  vendorOrder: (orderId: UUID) => `/spa/vendor/order/${orderId}`,
  vendorTaxCertifications: () => `/spa/vendor/tax_certifications`,
  vendorTaxCertification: (orderId: UUID) => `/spa/vendor/tax_certification/${orderId}`,
  vendorPayoffs: () => `/spa/vendor/payoffs`,
  vendorPayoff: (orderId: UUID) => `/spa/vendor/payoff/${orderId}`,

  // Incoming Transactions page by type
  incomingWires: () => '/spa/admin/accounting/incoming_transactions/wires',
  incomingChecks: () => '/spa/admin/accounting/incoming_transactions/checks',
  incomingAchs: () => '/spa/admin/accounting/incoming_transactions/achs',
  unrestrictedInternalTransfers: () => '/spa/admin/accounting/internal_transfers',

  // The Following URLS are Server Urls and should be moved to ServerUrls as time permits for testing.
  createOrderWith1003FileApi: () => `/lender/v1/1003`,

  achPushes: (orderId: UUID) => `/escrow/v1/ach_pushes/${orderId}`,

  //  Client Portal
  clientOrders: () => `/spa/client/orders`,

  clientOrderOverview: (orderId: UUID) => `/spa/client/order/${orderId}/overview`,
  clientOrderFees: (orderId: UUID) => `/spa/client/order/${orderId}/fees`,
  clientOrderTitle: (orderId: UUID) => `/spa/client/order/${orderId}/title`,

  inbox: (orderId: UUID) => `/spa/admin/order/${orderId}/inbox`,
};

const orderPages = {
  [eAccessControlUserType.Admin]: (orderId: string) => DefaultClientUrls.orderStatus(orderId),
  [eAccessControlUserType.Vendor]: (orderId: string) => DefaultClientUrls.vendorOrder(orderId),
  [eAccessControlUserType.Client]: (orderId: string) => DefaultClientUrls.clientOrderOverview(orderId),
};

export const ClientUrlFactory = (baseUrl?: string) => urlGeneratorService({}, baseUrl).addUrl(DefaultClientUrls);
export const clientUrls = ClientUrlFactory();
